import { List, Map, Record } from "immutable";
import { RootState, RootActions } from "common/reducers";
import { receive } from "common/reducers/Utils";
import { openingEstimationFromJSON, OpeningEstimationRecord } from "common/records/OpeningEstimation";
import {
  RECEIVE_OPENING_ESTIMATION,
  DUPLICATE_OPENING_ESTIMATION,
  DECREMENT_OPENING_ESTIMATION_UNSAVED_ID,
  OpeningEstimationActions,
} from "common/reducers/openingEstimation.actions";
import { estimateLineItem as estimateLineItemSelector } from "common/selectors/estimateLineItem.selectors";

export const OpeningEstimationStateRecord = Record({
  byId: Map<number, OpeningEstimationRecord>(),
  lastSavedById: Map<number, OpeningEstimationRecord>(),
  editingSnapshotById: Map<number, OpeningEstimationRecord>(), // Snapshot for line item rollback
  unsavedId: -1,
});

export const initialState = OpeningEstimationStateRecord();
export type OpeningEstimationState = typeof initialState;

const model = "openingEstimations";

export const openingEstimationReducer = (state: RootState, action: RootActions): RootState => {
  if (state && !state.getIn([model])) {
    state = state.setIn([model], initialState);
  }
  switch (action.type) {
    case RECEIVE_OPENING_ESTIMATION:
      return receive(state, model, openingEstimationFromJSON(action.payload.openingEstimation));

    case DECREMENT_OPENING_ESTIMATION_UNSAVED_ID: {
      const currentUnsavedId = state.getIn([model, "unsavedId"]) as number;
      return state.setIn([model, "unsavedId"], currentUnsavedId - 1);
    }

    case DUPLICATE_OPENING_ESTIMATION: {
      const { sourceOpeningEstimationId, targetLineItemId } = action.payload;

      const originalOpeningEstimation = state.getIn([model, "byId", sourceOpeningEstimationId]) as
        | OpeningEstimationRecord
        | undefined;
      if (!originalOpeningEstimation || originalOpeningEstimation._destroy) {
        return state;
      }

      const newOpeningEstimationId = state.getIn([model, "unsavedId"]) as number;
      state = openingEstimationReducer(state, OpeningEstimationActions.decrementUnsavedId());

      const duplicated = originalOpeningEstimation
        .set("id", newOpeningEstimationId)
        .set("estimate_line_item_id", targetLineItemId);
      state = state.setIn([model, "byId", newOpeningEstimationId], duplicated);

      const targetLineItemRecord = estimateLineItemSelector(state, { estimateLineItemId: targetLineItemId });
      if (targetLineItemRecord) {
        const updatedIds = (targetLineItemRecord.opening_estimation_ids ?? List<number>()).push(newOpeningEstimationId);
        state = state.setIn(["estimateLineItems", "byId", targetLineItemId, "opening_estimation_ids"], updatedIds);
      }

      return state;
    }

    default:
      return state;
  }
};
